import type { OperationLimit, SimulationLimits, SlippageQuote } from '@morpho-org/evm-simulation'
import type { BaseAction, TransactionAction } from '@morpho-org/morpho-sdk'

import { DEFAULT_SLIPPAGE_TOLERANCE, type MarketId, MathLib } from '@morpho-org/blue-sdk'
import { type Address, ethAddress } from 'viem'

const RAY = 10n ** 27n

type Subject =
  | { type: OperationLimit['type']; marketId: MarketId }
  | { type: OperationLimit['type']; vault: Address }
  | { type: 'vaultV1MigrateToV2'; sourceVault: Address; targetVault: Address }

const isPositive = (amount: bigint | undefined): amount is bigint =>
  amount !== undefined && amount > 0n

/** Shares floor implied by a bundle's `maxSharePrice` (RAY-scaled assets per share) guard. */
const minShares = (netAssets: bigint | undefined, maxSharePrice: bigint) =>
  isPositive(netAssets) && maxSharePrice > 0n
    ? MathLib.mulDivDown(netAssets, RAY, maxSharePrice)
    : undefined

/** morpho-sdk only accepts native funding for the full payment, so the paid asset is then ETH. */
const nativePaid = (nativeAmount: bigint | undefined) =>
  isPositive(nativeAmount) ? { assetPaid: ethAddress } : {}

/** Allocator penalties come out of the received loan assets. */
const netOfPenalty = (assets: bigint, penalty: bigint) =>
  assets > penalty ? assets - penalty : undefined

const limitFor = (
  subject: Subject,
  quote: Record<keyof SlippageQuote, bigint | undefined> | SlippageQuote,
  extra: { account?: Address; assetPaid?: Address } = {}
): OperationLimit | undefined => {
  const quoted = Object.fromEntries(
    Object.entries(quote).filter(([, amount]) => isPositive(amount))
  ) as SlippageQuote
  if (Object.keys(quoted).length === 0) return undefined
  return {
    ...subject,
    ...extra,
    quote: quoted,
    slippageTolerance: DEFAULT_SLIPPAGE_TOLERANCE
  } as OperationLimit
}

const toOperationLimit = (action: TransactionAction): OperationLimit | undefined => {
  // Blue actions expose the referral fee rate but not the fee amount taken from the user.
  if (
    action.type.startsWith('blue') &&
    isPositive((action.args as { referralFeePct?: bigint }).referralFeePct)
  ) {
    return undefined
  }
  switch (action.type) {
    case 'vaultV1Deposit':
    case 'vaultV2Deposit': {
      const { vault, amount, netAssets, maxSharePrice, nativeAmount } = action.args
      return limitFor(
        { type: action.type, vault },
        { assetsPaid: amount, sharesMinted: minShares(netAssets, maxSharePrice) },
        nativePaid(nativeAmount)
      )
    }
    case 'vaultV1Withdraw':
    case 'vaultV2Withdraw':
      return limitFor(
        { type: action.type, vault: action.args.vault },
        { assetsReceived: action.args.netAssets }
      )
    case 'vaultV1Redeem':
    case 'vaultV2Redeem':
      return limitFor(
        { type: action.type, vault: action.args.vault },
        { sharesBurned: action.args.shares }
      )
    case 'vaultV1MigrateToV2': {
      const { sourceVault, targetVault, shares, netAssets, maxSharePriceVaultV2 } = action.args
      return limitFor(
        { type: action.type, sourceVault, targetVault },
        { sharesBurned: shares, sharesMinted: minShares(netAssets, maxSharePriceVaultV2) }
      )
    }
    case 'blueSupply':
      return limitFor(
        { type: action.type, marketId: action.args.market as MarketId },
        { assetsPaid: action.args.assets },
        { account: action.args.onBehalf, ...nativePaid(action.args.nativeAmount) }
      )
    case 'blueWithdraw': {
      const { market, withdrawAssets, withdrawShares, reallocationPenaltyAssets, onBehalf } =
        action.args
      return limitFor(
        { type: action.type, marketId: market as MarketId },
        isPositive(withdrawShares)
          ? { sharesBurned: withdrawShares }
          : { assetsReceived: netOfPenalty(withdrawAssets, reallocationPenaltyAssets) },
        { account: onBehalf }
      )
    }
    case 'blueSupplyCollateral':
    case 'blueBorrow':
    case 'blueSupplyCollateralBorrow': {
      const {
        market,
        collateralAssets,
        borrowAssets,
        reallocationPenaltyAssets,
        nativeAmount,
        onBehalf
      } = action.args
      return limitFor(
        { type: action.type, marketId: market as MarketId },
        {
          assetsPaid: collateralAssets,
          assetsReceived: netOfPenalty(borrowAssets, reallocationPenaltyAssets)
        },
        { account: onBehalf, ...nativePaid(nativeAmount) }
      )
    }
    case 'blueRepay':
    case 'blueWithdrawCollateral':
    case 'blueRepayWithdrawCollateral': {
      const { market, repayAssets, repayShares, maxRepayAssets, collateralAssets, onBehalf } =
        action.args
      // Shares-mode repay burns exactly `repayShares` debt shares and pays at most `maxRepayAssets`.
      const byShares = isPositive(repayShares)
      return limitFor(
        { type: action.type, marketId: market as MarketId },
        {
          assetsPaid: byShares ? maxRepayAssets : repayAssets,
          sharesBurned: byShares ? repayShares : undefined,
          assetsReceived: collateralAssets
        },
        { account: onBehalf, ...nativePaid(action.args.nativeAmount) }
      )
    }
    // Refinance, in-kind and force exits carry no quotable amounts in their action args.
    default:
      return undefined
  }
}

/**
 * v5 slippage limits for a bundle's final action, quoted from the amounts morpho-sdk encoded into
 * it. `undefined` when there is nothing to quote, so the SDK skips the checks.
 */
export const buildSimulationLimits = (
  action: BaseAction | undefined
): SimulationLimits | undefined => {
  if (!action) return undefined
  const limit = toOperationLimit(action as TransactionAction)
  return limit ? { operations: [limit] } : undefined
}
